import Game from '../Game';
import Sprite from '../Prototype/Sprite';
import SpriteComponent from '../Component/Render/Sprite';
import { report } from '../report';
import { MathHelper } from '../Utility/MathHelper';

interface IAnimationFrame {
    resourceName: string;
    animationName: string;
    autoStart?: boolean;
    frameRate?: number;
    [key: string]: any;
}

interface IAnimationData {
    animations: { [key: string]: string[] };
}

export default class AnimationFrame extends Sprite {
    private props: IAnimationFrame;
    private frameNames: string[];
    private currentFrame: number = 0;
    private frameRate: number = 60;
    public spriteComponent: SpriteComponent;

    private interval: any;

    protected onCreate(props: IAnimationFrame): void {
        super.onCreate(props);
        this.spriteComponent = this.getComponent(SpriteComponent);
        this.props = props;
        this.frameRate = props.frameRate || this.frameRate;
        this.loadAnimation();
        if (this.frameNames?.length > 0) {
            if (this.props.autoStart) {
                this.start();
            }
        } else {
            report('no frames to be played', { once: true });
        }
    }

    private loadAnimation() {
        const { animationName, resourceName } = this.props;
        try {
            const resource = Game.resource.get(resourceName) as IAnimationData;
            if (!resource) {
                report("Given resource not found, please check if it's loaded", {
                    once: true,
                    tags: {
                        resourceName,
                        animationName,
                        existsOrQueued: Game.resource.existsOrQueued(resourceName),
                    },
                    fingerprint: 'FailedToFindAnimationData:loadAnimation',
                });
                return;
            }

            if (!resource.hasOwnProperty('animations')) {
                report('Given resource does not contain animations', {
                    once: true,
                    tags: {
                        resourceName,
                        animationName,
                        existsOrQueued: Game.resource.existsOrQueued(resourceName),
                    },
                    fingerprint: 'ResourceDoesNotContainAnimationData:loadAnimation',
                });
                return;
            }
            this.frameNames = resource.animations[animationName];
        } catch (error) {
            report('loadAnimation failed', {
                error,
                once: true,
                tags: { resourceName: this.props.resourceName, animationName: this.props.animationName },
                fingerprint: 'FailedToLoadAnimation:loadAnimation',
            });
        }
    }

    private readonly updateFrame = () => {
        if (this.destroyed || !this.spriteComponent) return;
        this.spriteComponent.texture = this.frameNames[this.currentFrame];
        this.currentFrame = (this.currentFrame + 1) % this.frameNames.length;
    };

    public start() {
        this.clearInterval();
        this.interval = Game.ticker.setInterval(this.updateFrame, 1000 / this.frameRate);
    }

    private clearInterval() {
        if (this.interval !== null) {
            Game.ticker.clearInterval(this.interval);
            this.interval = null;
        }
    }

    public *playOneLoop(durationMs: number): IterableIterator<number> {
        let elapsed: number = 0;
        const maxFrame = this.frameNames.length - 1;
        let lastFrame: number | null = null;
        while (true) {
            const currentFrame = MathHelper.repeat(
                Math.floor(MathHelper.lerp(0, maxFrame, elapsed / durationMs)),
                this.frameNames.length
            );
            if (lastFrame !== currentFrame) {
                lastFrame = currentFrame;
                this.spriteComponent.texture = this.frameNames[currentFrame];
            }
            if (elapsed >= durationMs) break;
            const dt: number = yield;
            // Fall back to 0 in case iterator is misused and does not get dt
            elapsed += dt ?? 0;
        }
        // Ensure it ends up on last frame even if there are lags
        if (lastFrame !== maxFrame) {
            lastFrame = maxFrame;
            this.spriteComponent.texture = this.frameNames[maxFrame];
        }
    }

    public stop() {
        this.clearInterval();
        this.currentFrame = 0;
    }

    public destroy() {
        this.clearInterval();
        super.destroy();
    }
}
